import { useCallback } from 'react'
import { FloatingSaveControls } from '@renderer/components/floating-save-controls'
import { useCmdEnterSave } from '@renderer/hooks/use-cmd-enter-save'
import type { StagedAction } from '../../../hooks/use-staged-actions'
import type { ActionInlineDraft } from '../inline-drafts'
import { DraftSupersededWarning, SaveErrorBanner, useActionSaveSurface } from './save-surface'

/**
 * Dirty-state footer (HIG-5830): a centered discard circle + green "Save
 * changes" CTA replace the control row while the view holds an unsent body
 * edit, so Execute is unreachable until the draft is saved or dropped. Titles
 * commit on blur and only reach here as a retry after that commit failed.
 */
export function ActionViewDirtyControls({
  action,
  draft,
  enableHotkey = true,
  onSaved,
}: {
  action: StagedAction
  draft: ActionInlineDraft | undefined
  enableHotkey?: boolean
  /** Fires after a save persists — not on failure, and not on discard. */
  onSaved?: () => void
}) {
  const { handleSave, handleDiscard, isBusy, errorMessage, isSuperseded } = useActionSaveSurface(action, draft)

  const save = useCallback(async () => {
    if (await handleSave()) onSaved?.()
  }, [handleSave, onSaved])

  // ⌘↵ saves while dirty — this region replaces the control row, so the
  // execute hotkey is out of scope until the draft resolves.
  useCmdEnterSave(enableHotkey && !isBusy, save)

  return (
    <FloatingSaveControls onSave={() => void save()} onDiscard={handleDiscard} isBusy={isBusy} className="flex-1">
      {errorMessage && <SaveErrorBanner message={errorMessage} />}
      {isSuperseded && <DraftSupersededWarning className="self-stretch" />}
    </FloatingSaveControls>
  )
}
